<template>
  <v-dialog
    :model-value="compareOpen"
    fullscreen
    @update:model-value="closeCompare"
  >
    <v-sheet class="compare-modal d-flex flex-column h-100">
      <v-sheet
        class="d-flex flex-shrink-0 border-b-sm py-2"
        min-height="58px"
      >
        <div class="w-50 px-6 d-flex align-center ga-4 flex-wrap">
          <template v-if="hasRevisions">
            <v-btn
              icon="mdi-chevron-left"
              variant="text"
              size="small"
              :disabled="!canGoNewer"
              title="Newer revision"
              aria-label="Newer revision"
              @click="goNewer"
            />
            <div class="flex-grow-1 min-w-0">
              <RevisionListItem
                v-if="currentRevision"
                :revision="{ ...currentRevision, index: currentOldIndex }"
              />
            </div>
            <DisableRbac :permissions="['restoreDraftProfileRevision']">
              <v-btn
                v-if="canRestore"
                color="primary"
                variant="flat"
                rounded="lg"
                size="small"
                :disabled="revisionsMatch || modalParams.jobProfileCompare?.readOnly"
                @click="requestRestore"
              >
                Restore this revision
              </v-btn>
            </DisableRbac>
            <v-btn
              icon="mdi-chevron-right"
              variant="text"
              size="small"
              :disabled="!canGoOlder"
              title="Older revision"
              aria-label="Older revision"
              @click="goOlder"
            />
          </template>
          <span
            v-else
            class="text-body-1"
          >
            Compare Profiles
          </span>
        </div>
        <div class="w-50 px-6 d-flex align-center ga-4">
          <div class="flex-grow-1">
            <RevisionListItem
              v-if="hasRevisions && currentNewRevision"
              :revision="{ ...currentNewRevision, index: currentNewIndex }"
            />
          </div>
          <DisableRbac :permissions="['applyProfileTemplate']">
            <v-btn
              v-if="canApplyTemplateUpdate"
              color="primary"
              variant="flat"
              rounded="lg"
              size="small"
              @click="openApplyTemplateConfirm"
            >
              Apply Template Update
            </v-btn>
          </DisableRbac>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            aria-label="Close"
            @click="closeCompare"
          />
        </div>
      </v-sheet>

      <div
        v-if="loading"
        class="flex-grow-1 d-flex justify-center align-center"
      >
        <v-progress-circular
          :size="40"
          :width="3"
          color="primary"
          indeterminate
        />
      </div>

      <div
        v-else
        class="flex-grow-1 min-h-0 d-flex overflow-hidden"
      >
        <ComparePane
          :profile="oldProfile"
          :fields="oldDiffFields"
          :subtitle="oldProfileSubtitle"
          :meta-html="oldMetaDiffHtml"
          :is-current-version="oldVersionIsCurrentVersion"
          :is-diff-pane="true"
          :title-html="oldTitleDiffHtml"
          :only-compare-locked="canApplyTemplateUpdate"
          right-border
          aria-label="Older revision"
        />
        <ComparePane
          :profile="newProfile"
          :fields="newDiffFields"
          :subtitle="newProfileSubtitle"
          :meta-html="newMetaDiffHtml"
          :is-current-version="newVersionIsCurrentVersion"
          :is-diff-pane="true"
          :title-html="newTitleDiffHtml"
          :only-compare-locked="canApplyTemplateUpdate"
          aria-label="Newer revision"
        />
      </div>
    </v-sheet>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useModals } from '@/stores/modals/modals';
import { useJobProfileCompare } from '@/composables/useJobProfileCompare';
import ComparePane from '@/components/library/employeeAcknowledgements/ComparePane.vue';
import RevisionListItem from '@ui/components/editor/RevisionList/RevisionListItem.vue';
import DisableRbac from '@/components/rbac/DisableRbac.vue';

const { show, modalParams } = storeToRefs(useModals());
const { setModal } = useModals();
const { addErrorSnackbar } = useAppSnackbar();

const compareOpen = computed(() => !!show.value.jobProfileCompare);
const oldSide = computed(() => modalParams.value.jobProfileCompare?.oldSide ?? null);
const newSide = computed(() => modalParams.value.jobProfileCompare?.newSide ?? null);
const revisions = computed(() => modalParams.value.jobProfileCompare?.revisions ?? []);
const hasRevisions = computed(() => revisions.value.length > 0);

const currentOldUlid = ref(null);

watch(compareOpen, (open) => {
  if (open) {
    currentOldUlid.value = modalParams.value.jobProfileCompare?.oldSide?.revisionUlid ?? null;
  }
}, { immediate: true });

const currentOldIndex = computed(() => revisions.value.findIndex((r) => r.ulid === currentOldUlid.value));
const currentRevision = computed(() => revisions.value[currentOldIndex.value] ?? null);

const currentNewIndex = computed(() => revisions.value.findIndex((r) => r.ulid === newSide.value?.revisionUlid));
const currentNewRevision = computed(() => revisions.value[currentNewIndex.value] ?? null);

const effectiveOldSide = computed(() => {
  const base = oldSide.value;

  if (!base || !currentOldUlid.value) return base ?? null;

  return { ...base, revisionUlid: currentOldUlid.value };
});

const canGoOlder = computed(() => (
  currentOldIndex.value >= 0 && currentOldIndex.value < revisions.value.length - 1
));
const canGoNewer = computed(() => currentOldIndex.value > 0);

const goOlder = () => {
  if (canGoOlder.value) {
    currentOldUlid.value = revisions.value[currentOldIndex.value + 1].ulid;
  }
};
const goNewer = () => {
  if (canGoNewer.value) {
    currentOldUlid.value = revisions.value[currentOldIndex.value - 1].ulid;
  }
};

const closeCompare = () => setModal({ field: 'jobProfileCompare', value: false });

// Restore is only available when the old side is a draft revision (not HRIS)
const canRestore = computed(() => effectiveOldSide.value && !effectiveOldSide.value.isHrisProfile);
const revisionsMatch = computed(
  () => effectiveOldSide.value?.revisionUlid === newSide.value?.revisionUlid,
);

const requestRestore = () => {
  setModal({
    field: 'restoreRevisionConfirm',
    value: true,
    params: { revisionUlid: effectiveOldSide.value?.revisionUlid },
  });
};

const openApplyTemplateConfirm = () => {
  setModal({ field: 'updateFromTemplateConfirm', value: true });
};

const {
  loading,
  loadError,
  oldProfile,
  newProfile,
  oldDiffFields,
  newDiffFields,
  oldTitleDiffHtml,
  newTitleDiffHtml,
  oldMetaDiffHtml,
  newMetaDiffHtml,
  oldVersionIsCurrentVersion,
  newVersionIsCurrentVersion,
  oldProfileSubtitle,
  newProfileSubtitle,
} = useJobProfileCompare({
  active: compareOpen,
  oldSide: effectiveOldSide,
  newSide,
});

const canApplyTemplateUpdate = computed(
  () => newProfile.value?.type_key === 'job_profile_template'
    && !!oldProfile.value
    && oldProfile.value.type_key !== 'job_profile_template',
);

watch(loadError, (newVal) => {
  if (newVal) {
    addErrorSnackbar({ text: newVal });
  }
}, { immediate: true });
</script>
